import { ReactNode, useLayoutEffect, useRef, useState } from "react";

import { cn } from "@utils/tailwind.util";

interface AnimatedHeightProps {
  children: ReactNode;
  className?: string;
}

// Animates its own height to whatever the content currently needs, so rows appearing or
// disappearing inside it slide the rest of the form instead of snapping it.
export function AnimatedHeight({ children, className }: AnimatedHeightProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const measuredRef = useRef<number | null>(null);
  const [height, setHeight] = useState<number | "auto">("auto");

  useLayoutEffect(() => {
    const content = contentRef.current;
    // jsdom and older browsers have no ResizeObserver; the content then just sizes itself.
    if (!content || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => {
      const next = entry.contentRect.height;
      if (measuredRef.current === next) return;

      measuredRef.current = next;
      setHeight(next);
    });
    observer.observe(content);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      style={{ height }}
      // Clipped vertically only, with room for a focus ring's 3px spread.
      className={cn(
        "w-full overflow-x-visible overflow-y-clip [overflow-clip-margin:4px]",
        "transition-[height] duration-500 ease-out",
        className
      )}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}
